import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Text,
  Button,
} from "@unifize/sarah";
import { toast } from "react-toastify";
import i18n from "i18next";
import k from "src/i18n/keys";
import ButtonLoader from "src/components/ButtonLoader";

import { getFormTitleOrNull } from "src/reducers";
import { deleteForm, searchForm } from "src/actions/forms";
import * as formApi from "src/api/forms";
import type { AppState } from "src/types";
import { Nullable } from "src/ts-types";
import type { FormDeletionModalState, FormDeletionModalSetter } from "./types";

interface Props {
  formDeletionModal: FormDeletionModalState;
  setFormDeletionModal: FormDeletionModalSetter;
}

const FormDeletionModal = ({
  formDeletionModal,
  setFormDeletionModal,
}: Props): Nullable<React.ReactElement> => {
  const dispatch = useDispatch();

  const modalShown = formDeletionModal.isOpen;
  const formId = formDeletionModal.formId;

  const formTitle = useSelector(({ app }: { app: AppState }) =>
    getFormTitleOrNull(app, formId),
  );

  const [loading, setLoading] = useState<boolean>(false);

  const handleClose = () => setFormDeletionModal({ isOpen: false });

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await formApi.deleteForm(id);
      dispatch(deleteForm(id));
      dispatch(searchForm(""));
      toast.success("Form successfully deleted");
    } catch (e: any) {
      console.error(e);
      toast.error(e.message);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  if (!modalShown) {
    return null;
  }

  const handleDeleteClick = () => {
    if (formId) {
      handleDelete(formId);
    }
  };

  return (
    <Modal
      height="auto"
      isOpen={modalShown}
      onClose={handleClose}
      position="center"
    >
      <ModalHeader>{i18n.t(k.DELETE_FORM)}</ModalHeader>
      <ModalContent>
        <Text variant="body" weight="regular">
          {`${i18n.t(k.ARE_YOU_SURE_YOU_WANT_TO_DELET)} ${formTitle || ""}?`}
        </Text>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          {i18n.t(k.CANCEL)}
        </Button>
        <ButtonLoader
          type="button"
          isLoading={loading}
          handleClick={handleDeleteClick}
        >
          {i18n.t(k.DELETE)}
        </ButtonLoader>
      </ModalFooter>
    </Modal>
  );
};

export default FormDeletionModal;
